import { PktHeading } from '@/components/heading/Heading'

type HeadingLevel = 1 | 2 | 3 | 4 | 5 | 6
type HeadingSize = 'xlarge' | 'large' | 'medium' | 'small' | 'xsmall'
type HeadingWeight = 'light' | 'regular' | 'medium' | 'bold'

const getHeadingStyle = (level: HeadingLevel): HeadingSize => {
  switch (level) {
    case 1:
      return 'xlarge'
    case 2:
      return 'large'
    case 3:
      return 'medium'
    case 4:
      return 'small'
    case 5:
    case 6:
      return 'xsmall'
    default:
      return 'medium'
  }
}

export default function Heading() {
  const levels: HeadingLevel[] = [1, 2, 3, 4, 5, 6]
  const sizes: HeadingSize[] = ['xlarge', 'large', 'medium', 'small', 'xsmall']
  const weights: HeadingWeight[] = ['light', 'regular', 'medium', 'bold']

  const tekst10Ord = 'En liten sjøhest fniser, sender bobler som danser mot solstråler.'
  const tekst30Ord =
    'En glad klovnefisk suser gjennom anemonene og kiler seg fast, mens en dumbo-blekksprut vinker med store ører. En liten skilpadde nikker fornøyd og lar seg dytte av strømmen.'
  const tekst75Ord = `Havets lekeplass våkner til liv: en sjøhest med krøllete hale rir på tang som en karusell, mens klovnefiskene spiller gjemsel i korallhagen. En dumbo-blekksprut svever som en ballong og ler med ørene sine, og en liten skilpadde tar et rolig magadrag før den glir av gårde. Til slutt kommer en delfin i fart, blåser en regnbueboble og deler et vennlig plask. Alle smiler under bølgene.`

  const havDyrTekst = [tekst10Ord, tekst30Ord, tekst75Ord]

  return (
    <main className="page-main pkt-container">
      <h1>Heading og BodyText</h1>
      <div style={{ marginBottom: '1rem' }}>
        <PktHeading level={2} size="medium">
          <code>Heading</code> er en komponent og <code>BodyText</code> er en stylingklasse.
        </PktHeading>
      </div>
      <p>
        Heading komponent hvor man kan velge hvordan headingen skal se ut (styling/size) og hvilket nivå headingen skal
        være i html (h1-h6). Det er viktig å bruke riktig html-nivå på de forskjellige overskriftene for å sikre at
        headingen blir lest av skjermlesere i riktig rekkefølge og for søkemotoroptimalisering.
      </p>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
        {/* Ulike nivåer */}
        <section className="component">
          <h2>📚 Ulike nivåer/heading tags (prop: level)</h2>
          <ul style={{ marginBottom: '2rem' }}>
            <li>
              Headingen styles basert på <code>level</code>-propen, med mindre <code>size</code>-propen er sendt inn
            </li>
            <li>Level 1-6 tilsvarer h1-h6 i html</li>
            <li>
              <code>size</code>-propen kan overstyre standardstilen
            </li>
            <li>Bruk størrelser som gir mening i forhold til hierarki og visuell vekt</li>
            <li>Uten level-prop får headingen level 2</li>
            <li>
              <span className="pkt-txt-16-medium">De forskjellige nivåene får disse størrelsene:</span>
              <ul style={{ marginTop: 0 }}>
                <li>level = 1 gir stilen xlarge</li>
                <li>level = 2 gir stilen large</li>
                <li>level = 3 gir stilen medium</li>
                <li>level = 4 gir stilen small</li>
                <li>level = 5 gir stilen xsmall</li>
                <li>level = 6 gir stilen xsmall</li>
              </ul>
            </li>
            <li>
              <span className="pkt-txt-16-medium">
                Headingens størrelser er responsive med breakpoints på 575 px og 767px:
              </span>
              <br />
              <ol style={{ marginTop: 0 }} className="pkt-txt-16-regular">
                <li>70px | 54px | 40px | font-weight 400 | xlarge</li>
                <li>48px | 36px | 28px | font-weight 400 | large</li>
                <li>30px | 28px | 24px | font-weight 400 | medium</li>
                <li>24px | 20px | 18px | font-weight 500 | small</li>
                <li>22px | 18px | 16px | font-weight 500 | xsmall</li>
              </ol>
              <span className="pkt-txt-16-medium">
                Responsiviteten kan testes ved å endre størrelsen på browservinduet
              </span>
            </li>
            <li>
              <span className="pkt-txt-16-medium">
                Ved spesielle behov kan man endre fontvekten med <code>weight</code>-propen:
              </span>
              <ul style={{ marginTop: 0 }}>
                <li>weight: light gir fontvekt 300</li>
                <li>weight: regular gir fontvekt 400</li>
                <li>weight: medium gir fontvekt 500</li>
                <li>weight: bold gir fontvekt 700</li>
              </ul>
              <p className="pkt-txt-16-medium" style={{ maxWidth: '51rem' }}>
                Størrelsene og fontvektene i denne komponenten er satt for å følge en standardstil for headinge. Vi
                anbefaler derfor kun å endre fontvekt ved spesielle behov.
              </p>
            </li>
          </ul>
          {levels.map((level) => (
            <PktHeading key={level} level={level}>
              Heading: level {level} & default size
            </PktHeading>
          ))}
        </section>

        {/* Ulike størrelser */}
        <section className="component">
          <h2>🧥 H2 og ulike størrelser (prop: size)</h2>
          <ul>
            <li>
              default <em>size: medium</em>
            </li>
            <li>Her har vi headinger som er h2-tags i html, men med ulike størrelser visuelt</li>
            <li>Ulike størrelser: xsmall, small, medium, large, xlarge</li>
            <li>Bruk størrelser som gir mening i forhold til hierarki og visuell vekt</li>
          </ul>
          {sizes.map((size) => (
            <PktHeading key={size} size={size}>
              Heading: level 2 & size {size}
            </PktHeading>
          ))}
        </section>

        {/* Samme størrelse */}
        <section className="component">
          <h2>H1-h6 med samme størrelse (prop: size)</h2>
          <ul>
            <li>Her har vi headinger som er h1-h6-tags i html, men med samme størrelse small</li>
            <li>Uten size-prop får headingene størrelse medium</li>
          </ul>
          {levels.map((level) => (
            <PktHeading key={level} level={level} size="small">
              Heading: level {level} & size small
            </PktHeading>
          ))}
        </section>

        <section className="component">
          <h2>🧥 H2 og ulike fontvekter (prop: weight)</h2>
          <ul>
            <li>H2 med ulike fontvekter</li>
            <li>Ulike fontvekter: light, regular, medium, bold</li>
            <li>
              Weight-propen brukes kun ved spefiselle behov, da heading-komponentene er satt opp til å følge en
              standardstil størrelser og fontvekt
            </li>
          </ul>
          {weights.map((weight) => (
            <PktHeading key={weight} weight={weight}>
              Heading H2: weight {weight}
            </PktHeading>
          ))}
        </section>

        {/* Visuelt gjemt */}
        <section className="component">
          <h2>👀 Visuelt gjemt heading (prop: visuallyHidden)</h2>
          <p>
            Visuelt gjemt heading er en heading som ikke vises på siden, men er tilgjengelig for skjermlesere og
            søkemotorer. Inspiser meg/test meg med skjermleser! 🕵🏻‍♀️
          </p>
          <PktHeading size="large" level={3} visuallyHidden>
            Du fant meg! Jeg er en gjemt heading! tihi 🐱
          </PktHeading>
        </section>

        {/* NoSpacing */}
        <section className="component">
          <h2>🚀 Mellomrom over og under heading (prop: noSpacing)</h2>
          <p>
            Headingene får med seg default margin og padding pga. heading-taggene i html. Vi fjerner dette by default (
            <em>noSpacing: true</em>) for mer forutsigbar bruk. Slik ser headingene ut når "noSpacing" IKKE er satt:
          </p>
          {levels.map((level) => (
            <PktHeading key={level} size={getHeadingStyle(level)} level={level}>
              Heading: level {level}
            </PktHeading>
          ))}
        </section>

        {/* Align */}
        <section className="component">
          <h2>🌸 Tekstjustering (prop: align)</h2>
          <PktHeading size="medium" level={3} align="start">
            Start
          </PktHeading>
          <PktHeading size="medium" level={3} align="center">
            Center
          </PktHeading>
          <PktHeading size="medium" level={3} align="end">
            End
          </PktHeading>
        </section>

        {/* Lenker */}
        <section className="component">
          <h2>🔗 Lenker inni tittelen</h2>
          <p>
            Dette er kun en test for å sjekke <em>slot controlleren</em>!! Lenker inni tittelen er ikke tilgjengelig for
            skjermlesere og søkemotorer. Vi anbefaler å bruke <a href="#">pkt-link</a> separat fra headingen.
          </p>
          <PktHeading size="medium" level={3}>
            <a href="#">Lenke i tittel</a>
          </PktHeading>
        </section>

        {/* BodyText-klasser */}
        <section className="component" style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
          <PktHeading size="large" level={2}>
            💪🏻 Body Text klasser
          </PktHeading>
          <span className="pkt-txt-16-medium">
            Bodytext størrelser er responsive med breakpoints på 575 px og 767px:
          </span>
          <ul style={{ marginTop: 0 }} className="pkt-txt-16-regular">
            <li>24px | 22px | 20px | font-weight 300</li>
            <li>20px | 18px | 16px | font-weight 300</li>
            <li>16px | 14px | 14px | font-weight 300</li>
          </ul>
          <span>
            <PktHeading size="xsmall" level={3}>
              "Large" - 24-light
            </PktHeading>
            <p className="pkt-bodytext--large">{tekst30Ord}</p>
          </span>
          <span>
            <PktHeading size="xsmall" level={3}>
              "Medium" - 18-light
            </PktHeading>
            <p className="pkt-bodytext--medium">{tekst30Ord}</p>
          </span>
          <span>
            <PktHeading size="xsmall" level={3}>
              "Small" - 16-light
            </PktHeading>
            <p className="pkt-bodytext--small">{tekst30Ord}</p>
          </span>
        </section>

        {/* Bodytext med havdyr-tekster */}
        <section className="component" style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          <PktHeading size="small" level={2}>
            Bodytext-klasser - kort tekst
          </PktHeading>
          <p className="pkt-bodytext--large">{tekst10Ord}</p>
          <p className="pkt-bodytext--medium">{tekst10Ord}</p>
          <p className="pkt-bodytext--small">{tekst10Ord}</p>
        </section>

        <section className="component" style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          <PktHeading size="small" level={2}>
            Bodytext-klasser - medium tekst
          </PktHeading>
          <p className="pkt-bodytext--large">{tekst30Ord}</p>
          <p className="pkt-bodytext--medium">{tekst30Ord}</p>
          <p className="pkt-bodytext--small">{tekst30Ord}</p>
        </section>

        <section className="component" style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          <PktHeading size="small" level={2}>
            Bodytext-klasser - lang tekst
          </PktHeading>
          <p className="pkt-bodytext--large">{tekst75Ord}</p>
          <p className="pkt-bodytext--medium">{tekst75Ord}</p>
          <p className="pkt-bodytext--small">{tekst75Ord}</p>
        </section>
      </div>
    </main>
  )
}
